import { Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import { User } from '@/api/User';
import { UserAudit } from '@/api/UserAudit';
import { Role } from '@/api/Role';
import { Status } from '@/api/Status';
import { dateCell } from '@/components/DataTable';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext, DataTableRow as Row } from '@/components/DataTable';
import RoleBadge from '@/components/RoleBadge';
import StatusBadge from '@/components/StatusBadge';
import { DropDownMenu } from '@/components/DropDownMenu';

export const useUserColumns = (
    data: User[],
    audit: Map<number, UserAudit>,
    reportsTo: Map<number, number[]>,
    onEdit?: (id: number) => void,
): ColumnDef<User>[] => {
    return useMemo<ColumnDef<User>[]>(
        () => [
            {
                id: 'createDate',
                header: 'Erstellt',
                accessorFn: (row: User) => row.createdAt,
                cell: dateCell()
            },
            {
                id: 'name',
                header: 'Name',
                accessorFn: (row: User) => `${row.firstName} ${row.lastName}`,
            },
            {
                id: 'email',
                header: 'E-mail',
                accessorKey: 'email',
            },
            {
                id: 'reportsTo',
                header: 'Berichtet An',
                accessorFn: (row: User) => {
                    const managerId = reportsTo.get(row.id)?.[0];
                    if (managerId === undefined) return '-';
                    const manager = data.find(u => u.id === managerId);
                    return manager ? `${manager.firstName} ${manager.lastName}` : '-';
                },
            },
            {
                id: 'role',
                header: 'Rolle',
                accessorKey: 'role',
                cell: (info: CellContext<User, Role>) => {
                    let role = info.getValue();
                    return <RoleBadge role={role} />;
                }
            },
            {
                id: 'status',
                header: 'Status',
                accessorKey: 'status',
                cell: (info: CellContext<User, Status>) => {
                    let status = info.getValue();
                    return <StatusBadge status={status} />;
                }
            },
            {
                id: 'access',
                header: 'Letzer Zugriff',
                accessorKey: 'id',
                cell: (info: CellContext<User, number>) => {
                    let id = info.getValue();
                    return <Text>{audit.get(id)?.accessedAt.toLocaleString() ?? ''}</Text>
                },
                sortFn: (rowA: Row<User>, rowB: Row<User>, _): number => {
                    let a = rowA.original;
                    let b = rowB.original;
                    let aa = audit.get(a.id)?.accessedAt ?? new Date(0);
                    let bb = audit.get(b.id)?.accessedAt ?? new Date(0);
                    return aa < bb ? 1 : -1;
                }
            },
            ...(onEdit ? [{
                id: 'edit',
                enableSorting: false,
                cell: info => {
                    let row = info.row.original;
                    return (<DropDownMenu onMenuEdit={() => onEdit(row.id)} />)
                }
            } as ColumnDef<User>] : [])
        ], [audit, reportsTo, data, onEdit]
    );

};
